import React, { useEffect, useState } from 'react';
import { ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  dayQuantile,
  formatWeekLabel,
  CATEGORY_LABEL,
  CATEGORY_COLOR,
  CATEGORY_ORDER,
  type ActionCategory,
  type AdminRow,
  type AdminWeek,
  type OperativePayload,
  type SlaBreachedEntry,
} from '../../data/operativeData';

/**
 * Super-admin-only Operative Dashboard.
 *
 * Monitors ADMINS — their active time, actions, SLA compliance, and
 * 8-week usage. Modeled visually on the TDB Portal Activity dashboard:
 * warm-neutral palette, tight editorial typography, per-admin day
 * strip, stacked column chart of actions by category over 8 weeks.
 *
 * All styling is inline / scoped-className so it doesn't inherit the
 * app's tailwind-heavy chrome — the dashboard has a deliberately quieter
 * visual language than the rest of the app to signal "instrument panel,
 * not marketing page".
 */

interface OperativeDashboardViewProps {
  previewControls?: boolean;
}

// ---------------------------------------------------------------------------
// Palette + primitives (single-scope CSS — no tailwind for the chrome)
// ---------------------------------------------------------------------------

const STYLES = `
.op-root {
  --surface-1: #fcfcfb;
  --page: #f9f9f7;
  --ink-1: #0b0b0b;
  --ink-2: #52514e;
  --ink-3: #898781;
  --grid: #e1e0d9;
  --baseline: #c3c2b7;
  --ring: rgba(11, 11, 11, 0.10);
  --seq1: #cde2fb;
  --seq2: #86b6ef;
  --seq3: #3987e5;
  --seq4: #1c5cab;
  --seq5: #0d366b;
  --sla-good: #199e70;
  --sla-warn: #c98500;
  --sla-bad:  #d95926;
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
  color: var(--ink-1);
  background: var(--page);
  padding: 24px 16px 48px;
  min-height: 100%;
}
@media (prefers-color-scheme: dark) {
  .op-root {
    --surface-1: #1a1a19;
    --page: #0d0d0d;
    --ink-1: #ffffff;
    --ink-2: #c3c2b7;
    --ink-3: #898781;
    --grid: #2c2c2a;
    --baseline: #383835;
    --ring: rgba(255, 255, 255, 0.10);
    --seq1: #104281;
    --seq2: #1c5cab;
    --seq3: #2a78d6;
    --seq4: #5598e7;
    --seq5: #9ec5f4;
  }
}
.op-wrap { max-width: 1080px; margin: 0 auto; }
.op-root h1 { font-size: 20px; font-weight: 650; margin: 0 0 4px; }
.op-root h2 { font-size: 14px; font-weight: 650; margin: 28px 0 8px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-2); }
.op-sub  { color: var(--ink-2); font-size: 13px; margin: 0 0 6px; }
.op-note { color: var(--ink-3); font-size: 12px; margin: 0 0 18px; max-width: 860px; line-height: 1.55; }
.op-tiles { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 8px; }
.op-tile { background: var(--surface-1); border: 1px solid var(--ring); border-radius: 10px; padding: 12px 16px; min-width: 130px; flex: 1; }
.op-tile .lbl { font-size: 11px; color: var(--ink-2); text-transform: uppercase; letter-spacing: 0.04em; }
.op-tile .val { font-size: 26px; font-weight: 600; margin-top: 3px; font-variant-numeric: tabular-nums; }
.op-tile .hint { font-size: 11px; color: var(--ink-3); margin-top: 2px; }
.op-board { background: var(--surface-1); border: 1px solid var(--ring); border-radius: 12px; padding: 6px 14px; overflow-x: auto; }
.op-board table { border-collapse: collapse; width: 100%; min-width: 900px; }
.op-board th { font-size: 10px; font-weight: 600; color: var(--ink-3); text-align: left; padding: 8px 8px 6px; border-bottom: 1px solid var(--grid); text-transform: uppercase; letter-spacing: 0.04em; }
.op-board th.num { text-align: right; }
.op-board td { padding: 6px 8px; border-bottom: 1px solid var(--grid); font-size: 13px; vertical-align: middle; }
.op-board tr:last-child td { border-bottom: 0; }
.op-board tr.op-open > td { background: color-mix(in srgb, var(--grid) 45%, transparent); }
.op-user { white-space: nowrap; max-width: 235px; overflow: hidden; text-overflow: ellipsis; cursor: pointer; }
.op-caret { display: inline-block; width: 14px; color: var(--ink-3); margin-right: 4px; vertical-align: -2px; }
.op-badge { display: inline-block; font-size: 10px; font-weight: 650; letter-spacing: 0.03em; color: var(--ink-2); border: 1px solid var(--baseline); border-radius: 4px; padding: 1px 6px; margin-left: 7px; vertical-align: 1px; text-transform: uppercase; }
.op-strip { display: inline-flex; gap: 3px; }
.op-cell  { width: 15px; height: 15px; border-radius: 4px; background: var(--surface-1); border: 1px solid var(--grid); box-sizing: border-box; }
.op-cell.q1 { background: var(--seq2); border-color: transparent; }
.op-cell.q2 { background: var(--seq3); border-color: transparent; }
.op-cell.q3 { background: var(--seq4); border-color: transparent; }
.op-cell.q4 { background: var(--seq5); border-color: transparent; }
.op-dayhdr { display: inline-flex; gap: 3px; }
.op-dayhdr span { width: 15px; text-align: center; display: inline-block; font-size: 10px; color: var(--ink-3); }
.op-htrack { display: inline-block; width: 60px; height: 6px; background: color-mix(in srgb, var(--grid) 60%, transparent); border-radius: 3px; vertical-align: 2px; margin-right: 8px; overflow: hidden; }
.op-hfill { display: block; height: 100%; background: var(--baseline); border-radius: 3px; }
.op-num  { font-variant-numeric: tabular-nums; text-align: right; }
.op-stack { display: inline-flex; gap: 2px; align-items: center; height: 13px; margin-right: 8px; }
.op-segm { display: inline-block; height: 13px; border-radius: 2px; }
.op-sla { display: inline-flex; align-items: center; gap: 5px; font-variant-numeric: tabular-nums; }
.op-sla-good { color: var(--sla-good); }
.op-sla-warn { color: var(--sla-warn); }
.op-sla-bad  { color: var(--sla-bad); }
.op-legend { display: flex; gap: 14px; font-size: 12px; color: var(--ink-2); flex-wrap: wrap; align-items: center; margin-bottom: 10px; }
.op-key { display: inline-block; width: 10px; height: 10px; border-radius: 3px; margin-right: 5px; vertical-align: -1px; }
.op-expand { background: color-mix(in srgb, var(--grid) 28%, transparent); }
.op-expand td { padding: 12px 16px 14px; }
.op-subtbl { border-collapse: collapse; }
.op-subtbl th, .op-subtbl td { padding: 3px 10px 3px 0; font-size: 12px; color: var(--ink-2); }
.op-chart { background: var(--surface-1); border: 1px solid var(--ring); border-radius: 12px; padding: 20px 20px 12px; overflow-x: auto; }
.op-chart-title { font-size: 12px; font-weight: 600; color: var(--ink-2); margin: 0 0 10px; text-transform: uppercase; letter-spacing: 0.04em; }
.op-cols { display: flex; align-items: flex-end; gap: 24px; height: 200px; padding: 0 8px; border-bottom: 1px solid var(--baseline); min-width: 500px; }
.op-colwrap { display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; }
.op-coltotal { font-size: 11px; color: var(--ink-2); margin-bottom: 4px; font-variant-numeric: tabular-nums; }
.op-col { display: flex; flex-direction: column-reverse; gap: 2px; width: 26px; }
.op-colseg { width: 26px; }
.op-colseg.top { border-radius: 4px 4px 0 0; }
.op-xlabels { display: flex; gap: 24px; padding: 6px 8px 2px; min-width: 500px; }
.op-xlabels span { width: 26px; font-size: 10px; color: var(--ink-3); text-align: center; white-space: nowrap; }
.op-preview-banner { background: #fff4d6; border: 1px solid #f0d590; color: #6b4a00; border-radius: 8px; padding: 10px 14px; font-size: 13px; display: flex; gap: 8px; align-items: center; margin-bottom: 16px; }
.op-breach-panel { background: var(--surface-1); border: 1px solid color-mix(in srgb, var(--sla-bad) 30%, transparent); border-radius: 12px; overflow-x: auto; margin-bottom: 8px; }
.op-breach-header { display: flex; align-items: center; gap: 8px; padding: 12px 16px 8px; border-bottom: 1px solid var(--grid); }
.op-breach-header h3 { font-size: 13px; font-weight: 650; color: var(--sla-bad); text-transform: uppercase; letter-spacing: 0.06em; margin: 0; }
.op-breach-empty { padding: 16px; font-size: 13px; color: var(--ink-3); text-align: center; }
.op-breach-tbl { border-collapse: collapse; width: 100%; min-width: 920px; }
.op-breach-tbl th { font-size: 10px; font-weight: 600; color: var(--ink-3); text-align: left; padding: 7px 12px 5px; border-bottom: 1px solid var(--grid); text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; }
.op-breach-tbl td { padding: 7px 12px; border-bottom: 1px solid var(--grid); font-size: 13px; vertical-align: middle; }
.op-breach-tbl tr:last-child td { border-bottom: 0; }
.op-breach-tbl tbody tr { cursor: pointer; }
.op-breach-tbl tbody tr:hover td { background: color-mix(in srgb, var(--sla-bad) 6%, transparent); }
.op-breach-link { color: var(--ink-1); text-decoration: none; font-weight: 500; font-size: 13px; }
.op-breach-link:hover { text-decoration: underline; color: var(--seq3); }
.op-breach-overdue { color: var(--sla-bad); font-variant-numeric: tabular-nums; font-size: 13px; font-weight: 700; white-space: nowrap; }
.op-breach-status { display: inline-block; font-size: 10px; font-weight: 650; letter-spacing: 0.03em; color: var(--ink-2); border: 1px solid var(--baseline); border-radius: 4px; padding: 1px 6px; text-transform: uppercase; white-space: nowrap; }
.op-breach-assign { color: var(--ink-2); font-size: 12px; }
.op-sev-critical { color: #c0392b; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; }
.op-sev-serious  { color: #d97706; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; }
.op-sev-warning  { color: #6b7280; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; }
.op-tier-badge { display: inline-block; font-size: 10px; font-weight: 700; letter-spacing: 0.03em; padding: 2px 6px; border-radius: 4px; white-space: nowrap; }
.op-tier-A { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
.op-tier-B { background: #fef3c7; color: #92400e; border: 1px solid #fcd34d; }
.op-tier-C { background: #f3f4f6; color: #6b7280; border: 1px solid #e5e7eb; }
.op-msg-preview { color: var(--ink-3); font-size: 11px; max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block; margin-top: 2px; }
.op-breach-partner { color: var(--ink-2); font-size: 12px; white-space: nowrap; }
.op-breach-date { color: var(--ink-3); font-size: 11px; white-space: nowrap; }
.op-toggle-group { display: inline-flex; border: 1px solid var(--grid); border-radius: 6px; overflow: hidden; margin-left: 10px; vertical-align: middle; }
.op-toggle-btn { padding: 3px 14px; font-size: 11px; font-weight: 650; cursor: pointer; border: none; background: var(--surface-1); color: var(--ink-3); letter-spacing: 0.03em; line-height: 1.6; }
.op-toggle-btn.op-active { background: var(--ink-1); color: var(--surface-1); }
.op-h2-row { display: flex; align-items: center; margin: 28px 0 8px; }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Sun–Sat, matching the "Weeks run Sun–Sat" contract stated in the page header
 * and the `currentWeek` range the backend returns.
 *
 * This was previously [1..5] (Mon–Fri), which meant Saturday and Sunday activity
 * was never rendered at all. On a Sunday — the first day of the week — that made
 * every admin row show "—" and the whole dashboard look broken even though the
 * activity data was arriving normally.
 */
const DAYS_SUN_SAT: number[] = [0, 1, 2, 3, 4, 5, 6];

/** Bucket label for breaches with no assignee. Kept distinct from any real email. */
const UNASSIGNED_KEY = '__unassigned__';

/**
 * The Operative Dashboard is super-admin-only and now reports out-of-SLA work
 * BY ADMIN, so it always needs the fleet-wide set. The old Mine/All toggle was
 * removed with the per-lead panel — "Mine" was always empty for a super admin.
 */
const ASSIGNEE_SCOPE = 'all' as const;
const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function formatHours(h: number): string {
  if (h === 0) return '—';
  if (h < 1) return `${(h * 60).toFixed(0)}m`;
  const rounded = Math.round(h * 2) / 2;
  return `${rounded}h`;
}

function slaClassFor(v: number): string {
  if (v >= 0.90) return 'op-sla-good';
  if (v >= 0.70) return 'op-sla-warn';
  return 'op-sla-bad';
}

function ROLE_BADGE_TEXT(role: AdminRow['role']): string {
  return role === 'super_admin' ? 'Super' : role === 'admin_manager' ? 'Manager' : 'Admin';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Sun–Sat strip, one cell per day, coloured by request-count quantile. */
function DayStrip({ week }: { week: AdminWeek }) {
  const peak = Math.max(
    0,
    ...Object.values(week.days).map((d) => d.requests),
  );
  return (
    <span className="op-strip" aria-label="Activity by day">
      {DAYS_SUN_SAT.map((dow) => {
        const day = week.days[dow];
        const q = day ? dayQuantile(day.requests, peak) : 0;
        const cls = q === 0 ? 'op-cell' : `op-cell q${q}`;
        const title = day
          ? `${DAY_LETTERS[dow]} · ${day.requests} req · ${formatHours(day.activeHours)}`
          : `${DAY_LETTERS[dow]} · no activity`;
        return <span key={dow} className={cls} title={title} />;
      })}
    </span>
  );
}

/** Compact horizontal bar showing this-week active hours vs the fleet peak. */
function ActiveTimeBar({ hours, peak }: { hours: number; peak: number }) {
  const pct = peak > 0 ? Math.min(100, Math.round((hours / peak) * 100)) : 0;
  return (
    <span>
      <span className="op-htrack" title={`${formatHours(hours)} active this week`}>
        <span className="op-hfill" style={{ width: `${pct}%` }} />
      </span>
      <span className="op-num">{formatHours(hours)}</span>
    </span>
  );
}

/** Stacked segment bar of actions this week, by category. */
function ActionStack({ week, totalPeak }: { week: AdminWeek; totalPeak: number }) {
  const total = week.totalActions;
  const widthPx = totalPeak > 0 ? Math.round((total / totalPeak) * 120) : 0;
  return (
    <span className="op-stack" title={`${total} actions this week`}>
      {CATEGORY_ORDER.map((cat) => {
        const n = week.categories[cat] ?? 0;
        if (n === 0) return null;
        const segWidth = total > 0 ? Math.max(2, Math.round((n / total) * widthPx)) : 0;
        return (
          <span
            key={cat}
            className="op-segm"
            style={{ width: `${segWidth}px`, background: CATEGORY_COLOR[cat] }}
            title={`${CATEGORY_LABEL[cat]}: ${n}`}
          />
        );
      })}
      <span className="op-num" style={{ marginLeft: 4 }}>{total}</span>
    </span>
  );
}

function ExpandedRow({ admin }: { admin: AdminRow }) {
  return (
    <table className="op-subtbl">
      <thead>
        <tr>
          <th style={{ textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Week
          </th>
          <th>Requests</th>
          <th>Active</th>
          <th>Online</th>
          <th>Actions</th>
          {CATEGORY_ORDER.map((c) => <th key={c}>{CATEGORY_LABEL[c]}</th>)}
        </tr>
      </thead>
      <tbody>
        {admin.weeks.map((w) => (
          <tr key={w.weekStart}>
            <td style={{ whiteSpace: 'nowrap' }}>{formatWeekLabel(w.weekStart)}</td>
            <td className="op-num">{w.requests.toLocaleString()}</td>
            <td className="op-num">{formatHours(w.activeHours)}</td>
            <td className="op-num">{formatHours(w.onlineHours)}</td>
            <td className="op-num">{w.totalActions}</td>
            {CATEGORY_ORDER.map((c) => (
              <td key={c} className="op-num">{w.categories[c] ?? '—'}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// SLA Breach Panel helpers
// ---------------------------------------------------------------------------

function hoursLabel(h: number): string {
  const d = Math.floor(h / 24);
  const rem = Math.round(h - d * 24);
  if (d > 0) return rem > 0 ? `${d}d ${rem}h` : `${d}d`;
  return `${Math.round(h)}h`;
}

function severityOf(hoursOverdue: number): { label: string; cls: string } {
  if (hoursOverdue >= 168) return { label: 'Critical', cls: 'op-sev-critical' };
  if (hoursOverdue >= 72)  return { label: 'Serious',  cls: 'op-sev-serious'  };
  return                          { label: 'Warning',  cls: 'op-sev-warning'  };
}

function tierOf(e: SlaBreachedEntry): { label: string; cls: string } {
  if (e.breachTier === 'fyi')    return { label: 'C-fyi',       cls: 'op-tier-C' };
  if (!e.lastCommentAt)          return { label: 'A-untouched', cls: 'op-tier-A' };
  return                                { label: 'B-unanswered',cls: 'op-tier-B' };
}

function relDate(isoStr: string | null): string {
  if (!isoStr) return 'never';
  const diff = Date.now() - new Date(isoStr).getTime();
  const d = Math.floor(diff / 86_400_000);
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

function lastActivityDate(e: SlaBreachedEntry): string | null {
  const a = e.lastCommentAt;
  const b = e.lastAdminAckAt;
  if (!a && !b) return null;
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

function submitDate(isoStr: string | null): string {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: '2-digit', timeZone: 'UTC',
  });
}

// ---------------------------------------------------------------------------
// SLA Breach Panel
// ---------------------------------------------------------------------------

/**
 * Out of SLA — BY ADMIN.
 *
 * Replaces the old per-lead "Needs action — your assigned leads" panel, which
 * was structurally useless on this screen: the Operative Dashboard is
 * super-admin-only, and JV submissions are never assigned to a super admin, so
 * the "mine" view was permanently empty.
 *
 * What a super admin actually needs is accountability per admin: who is sitting
 * on breached leads, how many, and how far past the window the worst one is.
 */
function OutOfSlaByAdmin({
  entries,
  admins,
}: {
  entries: SlaBreachedEntry[];
  admins: AdminRow[];
}) {
  const byAssignee = new Map<
    string,
    { count: number; real: number; fyi: number; worstHours: number }
  >();

  for (const e of entries) {
    const key = e.assignedTo ?? UNASSIGNED_KEY;
    const row =
      byAssignee.get(key) ?? { count: 0, real: 0, fyi: 0, worstHours: 0 };
    row.count += 1;
    if (e.breachTier === 'real') row.real += 1;
    else row.fyi += 1;
    row.worstHours = Math.max(row.worstHours, e.hoursOverdue);
    byAssignee.set(key, row);
  }

  const displayNameFor = (email: string): string => {
    if (email === UNASSIGNED_KEY) return 'Unassigned';
    return admins.find((a) => a.email === email)?.displayName ?? email;
  };

  // Worst offenders first; unassigned always last so it can't mask a person.
  const rows = [...byAssignee.entries()].sort((a, b) => {
    if (a[0] === UNASSIGNED_KEY) return 1;
    if (b[0] === UNASSIGNED_KEY) return -1;
    return b[1].real - a[1].real || b[1].worstHours - a[1].worstHours;
  });

  return (
    <div className="op-breach-panel" data-testid="sla-by-admin-panel">
      <div className="op-breach-header">
        <AlertTriangle size={14} />
        <h3>Out of SLA by admin ({entries.length})</h3>
      </div>
      {rows.length === 0 ? (
        <div className="op-breach-empty" data-testid="sla-by-admin-empty">
          No admin is holding an out-of-SLA lead.
        </div>
      ) : (
        <table className="op-breach-tbl">
          <thead>
            <tr>
              <th>Admin</th>
              <th className="num">Out of SLA</th>
              <th className="num">Needs answer</th>
              <th className="num">FYI</th>
              <th className="num">Worst overdue</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([email, r]) => (
              <tr key={email} data-testid={`sla-by-admin-row-${email}`}>
                <td>{displayNameFor(email)}</td>
                <td className="op-num" data-testid={`sla-by-admin-count-${email}`}>
                  {r.count}
                </td>
                <td className="op-num op-sla-bad">{r.real}</td>
                <td className="op-num">{r.fyi}</td>
                <td className="op-num">
                  <span className="op-breach-overdue">
                    {hoursLabel(r.worstHours)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export function OperativeDashboardView({ previewControls: _previewControls = false }: OperativeDashboardViewProps) {
  const [data, setData] = useState<OperativePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [lookback, setLookback] = useState<'week' | '7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/admin/operative/activity?assigneeFilter=${ASSIGNEE_SCOPE}&lookback=${lookback}`,
          { headers: { Authorization: `Bearer ${idToken}` } },
        );
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        if (!cancelled) setData(await res.json());
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load operative data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lookback]);

  const toggle = (email: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-tdb-accent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
        <AlertTriangle className="w-10 h-10 text-red-500" />
        <p className="text-gray-700 font-semibold">Failed to load operative data</p>
        {error && <p className="text-sm text-gray-500">{error}</p>}
      </div>
    );
  }

  const activeHoursPeak = Math.max(0, ...data.admins.map((a) => a.thisWeekActiveHours));
  const actionsPeak = Math.max(0, ...data.admins.map((a) => a.thisWeekActions));
  const chartPeak = Math.max(0, ...data.fleetActionsByWeek.map((w) => w.total));

  return (
    <>
      <style>{STYLES}</style>
      <div className="op-root">
        <div className="op-wrap">

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
            <h1 style={{ margin: 0 }}>Operative Dashboard</h1>
            <div className="op-toggle-group" data-testid="lookback-toggle" style={{ marginLeft: 0 }}>
              {(['week', '7d', '30d', '90d'] as const).map((lb) => (
                <button
                  key={lb}
                  className={`op-toggle-btn${lookback === lb ? ' op-active' : ''}`}
                  onClick={() => setLookback(lb)}
                  data-testid={`lookback-${lb}`}
                >
                  {lb === 'week' ? 'This week' : lb === '7d' ? '7 days' : lb === '30d' ? '30 days' : '90 days'}
                </button>
              ))}
            </div>
          </div>
          <p className="op-sub">
            Admin activity, active time, and per-week usage. UTC. Click any admin to expand their week-by-week history.
          </p>
          <p className="op-note">
            <b>Active time</b> counts half-hour blocks where the account did something beyond the app&rsquo;s automatic background polling — an idle open tab scores zero. Hover day cells for details; write actions are described by type only, never content. Weeks run Sun–Sat. <b>SLA compliance</b> is the share of new leads an admin owned that were contacted within the SLA window (72h new / 48h existing).
          </p>

          {/* Tiles */}
          <div className="op-tiles">
            <div className="op-tile">
              <div className="lbl">Active admins</div>
              <div className="val">{data.totals.activeAdmins}</div>
              <div className="hint">of {data.admins.length} this week</div>
            </div>
            <div className="op-tile">
              <div className="lbl">Fleet actions</div>
              <div className="val">{data.totals.totalActionsThisWeek}</div>
              <div className="hint">this week</div>
            </div>
            <div className="op-tile">
              <div className="lbl">Active hours</div>
              <div className="val">{formatHours(data.totals.totalActiveHoursThisWeek)}</div>
              <div className="hint">across the team</div>
            </div>
            <div className="op-tile">
              <div className="lbl">SLA compliance</div>
              <div className={`val ${slaClassFor(data.totals.slaComplianceThisWeek)}`}>
                {Math.round(data.totals.slaComplianceThisWeek * 100)}%
              </div>
              <div className="hint">72h new / 48h existing SLA</div>
            </div>
            <div className="op-tile">
              <div className="lbl">SLA breaches</div>
              <div className="val op-sla-bad" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={20} />
                {data.totals.slaBreachesThisWeek}
              </div>
              <div className="hint">deals past SLA, uncontacted</div>
            </div>
          </div>

          {/* Out of SLA — BY ADMIN.
              The old per-lead "Needs action — your assigned leads" panel lived
              here. It was removed 2026-09-20: this view is super-admin-only and
              JV submissions are never assigned to a super admin, so it was
              permanently empty. Accountability per admin is the useful cut. */}
          <div className="op-h2-row">
            <h2 style={{ margin: 0 }}>Out of SLA by admin</h2>
            {loading && (
              <span style={{ marginLeft: 10, color: 'var(--ink-3)', fontSize: 12 }}>Loading…</span>
            )}
          </div>
          <OutOfSlaByAdmin entries={data.slaBreached ?? []} admins={data.admins} />

          {/* Admin activity table */}
          <h2>
            {lookback === 'week'
              ? `This week (${formatWeekLabel(data.currentWeek.start)} – ${formatWeekLabel(data.currentWeek.end)})`
              : lookback === '7d' ? 'Last 7 days'
              : lookback === '30d' ? 'Last 30 days'
              : 'Last 90 days'}
          </h2>
          <div className="op-legend">
            {CATEGORY_ORDER.map((c) => (
              <span key={c}>
                <span className="op-key" style={{ background: CATEGORY_COLOR[c] }} />
                {CATEGORY_LABEL[c]}
              </span>
            ))}
          </div>
          <div className="op-board">
            <table>
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>
                    <span className="op-dayhdr">
                      {DAYS_SUN_SAT.map((d, i) => <span key={`${d}-${i}`}>{DAY_LETTERS[d]}</span>)}
                    </span>
                  </th>
                  <th className="num">Active</th>
                  <th>Actions</th>
                  <th className="num">Owned</th>
                  <th className="num">Closed</th>
                  <th className="num">SLA</th>
                </tr>
              </thead>
              <tbody>
                {data.admins.map((admin) => {
                  const isOpen = expanded.has(admin.email);
                  return (
                    <React.Fragment key={admin.email}>
                      <tr className={isOpen ? 'op-open' : undefined}>
                        <td>
                          <span className="op-user" onClick={() => toggle(admin.email)}>
                            <span className="op-caret">
                              {isOpen
                                ? <ChevronDown size={12} style={{ verticalAlign: -1 }} />
                                : <ChevronRight size={12} style={{ verticalAlign: -1 }} />}
                            </span>
                            <span style={{ fontWeight: 600 }}>{admin.displayName}</span>
                            <span className="op-badge">{ROLE_BADGE_TEXT(admin.role)}</span>
                          </span>
                        </td>
                        <td><DayStrip week={admin.weeks[0]} /></td>
                        <td>
                          <ActiveTimeBar hours={admin.thisWeekActiveHours} peak={activeHoursPeak} />
                        </td>
                        <td><ActionStack week={admin.weeks[0]} totalPeak={actionsPeak} /></td>
                        <td className="op-num">{admin.ownedDeals}</td>
                        <td className="op-num">{admin.closedAttributed}</td>
                        <td className="op-num">
                          {admin.slaCompliance === null
                            ? <span style={{ color: 'var(--ink-3)' }}>—</span>
                            : <span className={slaClassFor(admin.slaCompliance)}>
                                {Math.round(admin.slaCompliance * 100)}%
                              </span>}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="op-expand">
                          <td colSpan={7}>
                            <ExpandedRow admin={admin} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Fleet actions by week */}
          <h2>All admin actions by type per week</h2>
          <div className="op-chart">
            <p className="op-chart-title">Stacked by action category · last 8 weeks</p>
            <div className="op-cols">
              {data.fleetActionsByWeek.map((w) => {
                const totalHeight = 180;
                return (
                  <div key={w.weekStart} className="op-colwrap">
                    <div className="op-coltotal">{w.total || ''}</div>
                    <div className="op-col">
                      {CATEGORY_ORDER.slice().reverse().map((cat, idx, arr) => {
                        const n = w.byCategory[cat] ?? 0;
                        if (n === 0) return null;
                        const h = chartPeak > 0 ? Math.max(2, Math.round((n / chartPeak) * totalHeight)) : 0;
                        return (
                          <div
                            key={cat}
                            className={`op-colseg ${idx === arr.length - 1 ? 'top' : ''}`}
                            style={{ height: `${h}px`, background: CATEGORY_COLOR[cat] }}
                            title={`${CATEGORY_LABEL[cat]}: ${n} (week of ${formatWeekLabel(w.weekStart)})`}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="op-xlabels">
              {data.fleetActionsByWeek.map((w) => (
                <span key={w.weekStart}>{formatWeekLabel(w.weekStart)}</span>
              ))}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
